import type { ImageEditInstance, ImageEditProvider } from '@/types';
import { NANO_BANANA_MODELS } from '@/lib/constants';

const NANO_BANANA_PATH = '/v1/api/generate';

function getProvider(instance: ImageEditInstance): ImageEditProvider {
    return instance.provider ?? 'relay';
}

function mapQualityToImageSize(quality: string): string {
    if (quality === 'hd') return '2K';
    if (quality === 'ultra' || quality === '4k') return '4K';
    return '1K';
}

function fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('图片读取失败'));
        reader.readAsDataURL(file);
    });
}

export function getSelectedImageEditInstances(instances: ImageEditInstance[]): ImageEditInstance[] {
    const hasExplicitSelection = instances.some(instance => instance.callEnabled !== undefined);
    return hasExplicitSelection
        ? instances.filter(instance => instance.callEnabled === true)
        : instances.slice(0, 1);
}

// 构建代理 URL（解决浏览器 CORS 限制）
export function proxyUrl(target: string): string {
    return `/api/proxy?target=${encodeURIComponent(target)}`;
}

function responseErrorDetail(status: number, responseText: string): string {
    const detail = responseText.trim();
    if (status === 500 && !detail) {
        return '本地 API 代理不可用。开发模式请同时运行 `npm run dev:api`，发布版请通过 run.bat 启动。';
    }
    return detail || '服务未返回错误详情';
}

// --- 响应类型 ---

export interface ImageResultItem {
    url?: string;
    b64_json?: string;
}

function normalizeImageResults(value: unknown, depth = 0): ImageResultItem[] {
    if (typeof value === 'string') {
        const result = value.trim();
        if (!result) return [];
        const dataUrl = result.match(/^data:image\/[\w.+-]+;base64,(.+)$/i);
        return [dataUrl ? { b64_json: dataUrl[1] } : { url: result }];
    }
    if (Array.isArray(value)) return value.flatMap(item => normalizeImageResults(item, depth + 1));
    if (!value || typeof value !== 'object' || depth > 3) return [];
    const record = value as Record<string, unknown>;
    for (const key of ['url', 'image_url', 'imageUrl', 'output_url', 'outputUrl', 'src']) {
        if (typeof record[key] === 'string') return normalizeImageResults(record[key], depth + 1);
    }
    for (const key of ['b64_json', 'b64Json', 'base64', 'image_base64', 'imageBase64']) {
        if (typeof record[key] === 'string' && record[key]) return [{ b64_json: String(record[key]).replace(/^data:image\/[\w.+-]+;base64,/i, '') }];
    }
    for (const key of ['data', 'images', 'results', 'outputs', 'output', 'artifacts']) {
        const nested = normalizeImageResults(record[key], depth + 1);
        if (nested.length > 0) return nested;
    }
    return [];
}

export interface JobResult {
    model: string;
    requested_model: string;
    ratio: string;
    requested_size: string;
    actual_size: string;
    billing_basis: string;
    data: ImageResultItem[];
    images?: ImageResultItem[];
    requested?: number;
    completed?: number;
    failed?: number;
    partial?: boolean;
    partial_complete?: boolean;
    retry_safe?: boolean;
    failed_indices?: number[];
    queued_ms?: number;
    upstream_ms?: number;
    total_ms?: number;
}

export interface ImageJobData {
    id: string;
    job_id: string;
    client_job_id?: string;
    status: 'queued' | 'running' | 'succeeded' | 'failed';
    operation: 'generation' | 'edit';
    model: string;
    requested_model?: string;
    ratio?: string;
    quality?: string;
    count?: number;
    result?: JobResult;
    error?: { message: string; detail?: string };
    created_at?: string;
    completed_at?: string;
}

// --- 创建文生图 Job（JSON body） ---

async function createGenerationJob(
    instance: ImageEditInstance,
    params: {
        prompt: string;
        ratio?: string;
        quality?: string;
        count?: number;
    },
): Promise<ImageJobData> {
    const baseUrl = instance.apiUrl.replace(/\/$/, '');
    const apiPath = instance.apiPath || '/api/relay/image-jobs/edits';
    const targetUrl = `${baseUrl}${apiPath}`;
    const url = proxyUrl(targetUrl);

    const body: Record<string, unknown> = {
        prompt: params.prompt,
        model: instance.model,
        ratio: params.ratio || instance.defaultRatio,
        quality: params.quality || instance.defaultQuality,
        count: params.count || instance.defaultCount,
    };

    console.log('[imageEditService] 创建文生图 Job:', {
        url: targetUrl,
        body,
    });

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${instance.apiKey}`,
        },
        body: JSON.stringify(body),
    });

    const responseText = await response.text();

    if (!response.ok) {
        console.error('[imageEditService] 创建 Job 失败:', response.status, responseText);
        throw new Error(
            `创建 Job 失败 (${response.status})\n` +
            `请求: POST ${targetUrl}\n` +
            `Body: ${JSON.stringify(body, null, 2)}\n` +
            `响应: ${responseErrorDetail(response.status, responseText)}`
        );
    }

    const json = JSON.parse(responseText);
    if (!json.ok) {
        throw new Error(`创建 Job 失败: ${json.message || responseText}`);
    }

    return json.data;
}

// --- 创建参考图编辑 Job（multipart/form-data） ---

async function createEditJob(
    instance: ImageEditInstance,
    params: {
        prompt: string;
        images: File[];
        ratio?: string;
        quality?: string;
        count?: number;
    },
): Promise<ImageJobData> {
    const baseUrl = instance.apiUrl.replace(/\/$/, '');
    const apiPath = instance.apiPath || '/api/relay/image-jobs/edits';
    const targetUrl = `${baseUrl}${apiPath}`;
    const url = proxyUrl(targetUrl);

    const formData = new FormData();
    formData.append('prompt', params.prompt);
    formData.append('model', instance.model);
    formData.append('ratio', params.ratio || instance.defaultRatio);
    formData.append('quality', params.quality || instance.defaultQuality);
    formData.append('count', String(params.count || instance.defaultCount));
    for (const file of params.images) {
        formData.append('image', file);
    }

    console.log('[imageEditService] 创建参考图编辑 Job:', {
        url: targetUrl,
        prompt: params.prompt,
        model: instance.model,
        imageCount: params.images.length,
        imageNames: params.images.map(f => f.name),
    });

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${instance.apiKey}`,
        },
        body: formData,
    });

    const responseText = await response.text();

    if (!response.ok) {
        console.error('[imageEditService] 创建编辑 Job 失败:', response.status, responseText);
        throw new Error(
            `创建编辑 Job 失败 (${response.status})\n` +
            `请求: POST ${targetUrl}\n` +
            `响应: ${responseErrorDetail(response.status, responseText)}`
        );
    }

    const json = JSON.parse(responseText);
    if (!json.ok) {
        throw new Error(`创建编辑 Job 失败: ${json.message || responseText}`);
    }

    return json.data;
}

// --- 轮询 Job 状态 ---

async function pollJob(
    instance: ImageEditInstance,
    jobId: string,
    onProgress?: (status: string) => void,
    intervalMs: number = 3000,
    maxAttempts: number = 120,
): Promise<ImageJobData> {
    const baseUrl = instance.apiUrl.replace(/\/$/, '');
    const targetUrl = `${baseUrl}/api/relay/image-jobs/${jobId}`;

    for (let i = 0; i < maxAttempts; i++) {
        const url = proxyUrl(targetUrl);
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${instance.apiKey}`,
            },
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => '');
            throw new Error(`轮询 Job 失败 (${response.status}): ${responseErrorDetail(response.status, errorText)}`);
        }

        const json = await response.json();
        const job: ImageJobData = json.data;

        if (onProgress) onProgress(job.status);

        if (job.status === 'succeeded') return job;
        if (job.status === 'failed') {
            const errMsg = job.error?.message || '未知错误';
            throw new Error(`Job 执行失败: ${errMsg}`);
        }

        // queued / running → 等待后重试
        await new Promise(resolve => setTimeout(resolve, intervalMs));
    }

    throw new Error('Job 轮询超时');
}

// --- 组合：创建 Job 并轮询至完成 ---

async function createNanoBananaJob(instance: ImageEditInstance, params: Parameters<typeof generateImage>[1]): Promise<ImageJobData> {
    const targetUrl = `${instance.apiUrl.replace(/\/$/, '')}${NANO_BANANA_PATH}`;
    const images = await Promise.all((params.images ?? []).map(fileToDataUrl));
    const ratio = params.ratio || instance.defaultRatio;
    const quality = mapQualityToImageSize(params.quality || instance.defaultQuality);
    const response = await fetch(proxyUrl(targetUrl), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${instance.apiKey}` },
        body: JSON.stringify({ model: instance.model, prompt: params.prompt, images, aspectRatio: ratio, imageSize: quality, replyType: 'json' }),
    });
    const responseText = await response.text();
    if (!response.ok) throw new Error(`NanoBanana 生成失败 (${response.status}): ${responseErrorDetail(response.status, responseText)}`);
    const json = JSON.parse(responseText) as Record<string, unknown>;
    if (json.status === 'failed' || json.status === 'violation') throw new Error(`NanoBanana 生成失败: ${String(json.error || json.status)}`);
    const data = normalizeImageResults(json.results ?? json.data ?? json.images ?? json.output);
    if (data.length === 0) throw new Error(`NanoBanana 未返回图片（状态: ${String(json.status ?? 'unknown')}）`);
    const id = String(json.id || `nano-banana-${Date.now()}`);
    return {
        id,
        job_id: id,
        status: 'succeeded',
        operation: images.length > 0 ? 'edit' : 'generation',
        model: instance.model,
        ratio,
        quality,
        count: data.length,
        result: { model: instance.model, requested_model: instance.model, ratio, requested_size: quality, actual_size: quality, billing_basis: '', data, images: data },
    };
}

export async function generateImage(
    instance: ImageEditInstance,
    params: {
        prompt: string;
        images?: File[];        // 可选：参考图
        ratio?: string;
        quality?: string;
        count?: number;
    },
    onProgress?: (status: string) => void,
): Promise<ImageJobData> {
    if (getProvider(instance) === 'nano-banana') {
        onProgress?.('running');
        return createNanoBananaJob(instance, params);
    }
    // 1. 创建 Job
    const job = params.images && params.images.length > 0
        ? await createEditJob(instance, {
            prompt: params.prompt,
            images: params.images,
            ratio: params.ratio,
            quality: params.quality,
            count: params.count,
        })
        : await createGenerationJob(instance, {
            prompt: params.prompt,
            ratio: params.ratio,
            quality: params.quality,
            count: params.count,
        });

    // 2. 如果创建后直接成功（不太可能但防御性处理）
    if (job.status === 'succeeded') return job;
    if (job.status === 'failed') {
        throw new Error(`Job 创建后即失败: ${job.error?.message || '未知错误'}`);
    }

    // 3. 轮询至完成
    return pollJob(instance, job.id, onProgress);
}

export async function generateImageWithFallback(
    instances: ImageEditInstance[],
    params: Parameters<typeof generateImage>[1],
    onProgress?: (status: string, instance: ImageEditInstance, order: number) => void,
): Promise<{ job: ImageJobData; instance: ImageEditInstance; order: number }> {
    const selected = getSelectedImageEditInstances(instances);
    if (selected.length === 0) throw new Error('未勾选可调用的 AI 图片编辑实例');

    const failures: string[] = [];
    for (let index = 0; index < selected.length; index++) {
        const instance = selected[index];
        try {
            const job = await generateImage(instance, params, status => onProgress?.(status, instance, index + 1));
            return { job, instance, order: index + 1 };
        } catch (error) {
            failures.push(`${index + 1}号 ${instance.name}: ${error instanceof Error ? error.message : String(error)}`);
        }
    }
    throw new Error(`全部 AI 图片编辑实例调用失败\n${failures.join('\n')}`);
}

// --- 获取可用模型列表 ---

export interface ImageModelInfo {
    id: string;
    name: string;
    verified: boolean;
    capabilities: {
        reference_image: boolean;
        response_generation?: boolean;
        qualities: string[];
        ratios: string[];
    };
}

export async function fetchModels(instance: ImageEditInstance): Promise<ImageModelInfo[]> {
    if (getProvider(instance) === 'nano-banana') {
        return NANO_BANANA_MODELS.map(id => ({ id, name: id, verified: true, capabilities: { reference_image: true, response_generation: true, qualities: ['standard', 'hd', 'ultra', '4k'], ratios: [] } }));
    }
    const baseUrl = instance.apiUrl.replace(/\/$/, '');
    const targetUrl = `${baseUrl}/api/relay/models?usage=image`;
    const url = proxyUrl(targetUrl);

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${instance.apiKey}`,
        },
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`获取模型列表失败 (${response.status}): ${responseErrorDetail(response.status, errorText)}`);
    }

    const json = await response.json();
    return json.data?.models ?? [];
}
